import React, { ReactElement, useEffect } from "react";
import { Select, SelectProps } from "@amzn/awsui-components-react";
import { useDispatch, useSelector } from "react-redux";
import { EditableCellProps } from "../porAsinList/asinListEdit";
import { RootState } from "../app/rootReducer";
import {
  addSelectedAnnounceDate,
  addSelectedOnlineStreetDate,
  CandidateDateStatus,
  CandidateDateType,
  CandidateDateWithDescriptor,
  SelectedAsinMarketplaceDate,
} from "./candidateDatesConnector";
import { trackButtonClick } from "../telemetry/telemetry";
import { getCountryCode } from "../../from-server/acs/acs-service";
import { serviceEpochMillisToString } from "../../util/dateUtils";

export interface SelectDateDropdownProps {
  asin: string;
  obfuscatedMarketplaceId: string;
  dateType: CandidateDateType;
}

export interface CandidateDateOption {
  dateDisplay: string;
  dateValue: string;
  descriptor?: string;
  country?: string;
}

const CandidateDateStatusDisplayMap = new Map<CandidateDateStatus, string>([
  ["unAssigned", "Unassigned"],
  ["noDatesAvailable", "No Dates Available"],
]);
function getUnassignedOrNDACandidateDateOption(
  date: CandidateDateWithDescriptor
): CandidateDateOption {
  return {
    dateDisplay: CandidateDateStatusDisplayMap.get(date.dateStatus!)!,
    dateValue: date.dateStatus!,
  };
}

function getCandidateDateOption(
  date: CandidateDateWithDescriptor
): CandidateDateOption {
  return {
    dateDisplay: serviceEpochMillisToString(date.dateValueInMillis),
    dateValue: date.dateValueInMillis!.toString(),
    descriptor: date.descriptor,
    country: date.marketplaceId
      ? getCountryCode(date.marketplaceId)!
      : undefined,
  };
}

function convertDatesToSelectOptions(
  dates: CandidateDateWithDescriptor[]
): SelectProps.Option[] {
  return dates
    .map((date) =>
      date.dateStatus
        ? getUnassignedOrNDACandidateDateOption(date)
        : getCandidateDateOption(date)
    )
    .map((option) => ({
      label: option.dateDisplay,
      value: option.dateValue,
      labelTag: option.country,
      description: option.descriptor,
    }));
}

export const SelectDateDropdown = (
  props: SelectDateDropdownProps & EditableCellProps
): ReactElement => {
  const dispatch = useDispatch();
  const { candidateDatesMap, isGettingCandidates } = useSelector(
    (state: RootState) => state.candidateDates
  );

  const getAllDatesFromKey = (key: string) => candidateDatesMap[key] || [];

  const options = convertDatesToSelectOptions(
    getAllDatesFromKey(
      `${props.asin}_${props.obfuscatedMarketplaceId}_${props.dateType}`
    )
  );
  const [selectedOption, setSelectedOption] = React.useState(options[0]);

  function dispatchAction(
    selectedValue: string | undefined,
    asin: string,
    obfuscatedMarketplaceId: string
  ) {
    const isSelectedValueInMillis =
      selectedValue && !isNaN(parseInt(selectedValue));
    const selectedDate: SelectedAsinMarketplaceDate = {
      asin: asin,
      obfuscatedMarketplaceId: obfuscatedMarketplaceId,
      dates: [
        {
          dateValueInMillis: isSelectedValueInMillis
            ? parseInt(selectedValue!)
            : undefined,
          dateStatus:
            selectedValue && !isSelectedValueInMillis
              ? (selectedValue! as CandidateDateStatus)
              : undefined,
          dateType: props.dateType,
        },
      ],
    };
    if (props.dateType == "announceDate") {
      dispatch(addSelectedAnnounceDate(selectedDate));
    }
    if (props.dateType == "onlineStreetDate") {
      dispatch(addSelectedOnlineStreetDate(selectedDate));
    }
  }

  // When we get the candidate dates back, we set them in candidateDatesMap, so
  // this subscribes to that value changing and resets the selected option.
  useEffect(() => {
    const newOptions = convertDatesToSelectOptions(
      getAllDatesFromKey(
        `${props.asin}_${props.obfuscatedMarketplaceId}_${props.dateType}`
      )
    );
    if (newOptions.length !== 0) {
      setSelectedOption(newOptions[0]);

      // Also set the default selected candidate so that all dates are sent to be recorded.
      dispatchAction(
        newOptions[0].value,
        props.asin,
        props.obfuscatedMarketplaceId
      );
    }
  }, [candidateDatesMap]);

  return (
    <Select
      disabled={isGettingCandidates}
      selectedOption={selectedOption}
      onChange={({ detail }) => {
        trackButtonClick("baselineTable", "candidateDateSelect", {
          asin: props.asin,
          mp: props.obfuscatedMarketplaceId,
          selected: detail.selectedOption.value,
        });
        dispatchAction(
          detail.selectedOption.value,
          props.asin,
          props.obfuscatedMarketplaceId
        );
        setSelectedOption(detail.selectedOption);
      }}
      options={options}
      selectedAriaLabel="Selected"
      expandToViewport={true}
      triggerVariant="option"
    />
  );
};
